/**
 * The central registry of keyboard shortcuts (docs/PLAN.md §2.5): shown by the `?` help sheet and
 * next to command-palette entries. Bindings live where the actions are (pages, widgets); this is
 * the single place that documents them. `mod` is ⌘ on macOS and Ctrl elsewhere.
 */
export type ShortcutGroup = 'general' | 'navigation' | 'editing' | 'view';

export interface ShortcutDef {
  id: ShortcutId;
  group: ShortcutGroup;
  /** Key caps; several combos are alternatives, unless `sequence` (pressed one after another). */
  combos: readonly (readonly string[])[];
  sequence?: boolean;
}

export const SHORTCUT_IDS = [
  'palette',
  'help',
  'exportReport',
  'moveDown',
  'moveUp',
  'open',
  'views',
  'toggleVariants',
  'toggleInspector',
  'assumptions',
  'createItem',
  'newVariant',
  'editValue',
  'duplicate',
  'delete',
  'mute',
  'solo',
  'undo',
  'redo',
  'realNominal',
  'monteCarlo',
] as const;
export type ShortcutId = (typeof SHORTCUT_IDS)[number];

export const SHORTCUTS: readonly ShortcutDef[] = [
  { id: 'palette', group: 'general', combos: [['mod', 'K']] },
  { id: 'help', group: 'general', combos: [['?']] },
  { id: 'exportReport', group: 'general', combos: [['mod', 'P']] },
  { id: 'moveDown', group: 'navigation', combos: [['J'], ['↓']] },
  { id: 'moveUp', group: 'navigation', combos: [['K'], ['↑']] },
  { id: 'open', group: 'navigation', combos: [['↵']] },
  { id: 'views', group: 'navigation', combos: [['1–8']] },
  { id: 'toggleVariants', group: 'navigation', combos: [['[']] },
  { id: 'toggleInspector', group: 'navigation', combos: [[']']] },
  { id: 'assumptions', group: 'navigation', combos: [['G'], ['A']], sequence: true },
  { id: 'createItem', group: 'editing', combos: [['C']] },
  { id: 'newVariant', group: 'editing', combos: [['⇧', 'C']] },
  { id: 'editValue', group: 'editing', combos: [['E']] },
  { id: 'duplicate', group: 'editing', combos: [['mod', 'D']] },
  { id: 'delete', group: 'editing', combos: [['⌫']] },
  { id: 'mute', group: 'editing', combos: [['M']] },
  { id: 'solo', group: 'editing', combos: [['S']] },
  { id: 'undo', group: 'editing', combos: [['mod', 'Z']] },
  { id: 'redo', group: 'editing', combos: [['⇧', 'mod', 'Z']] },
  { id: 'realNominal', group: 'view', combos: [['R']] },
  { id: 'monteCarlo', group: 'view', combos: [['⇧', 'M']] },
];

export const shortcutOf = (id: ShortcutId): ShortcutDef | undefined =>
  SHORTCUTS.find((s) => s.id === id);

/** ⌘ on Apple platforms, Ctrl elsewhere. */
export function modKey(): string {
  const platform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
    navigator.platform;
  return /mac|iphone|ipad/i.test(platform) ? '⌘' : 'Ctrl';
}
